import { cleanPhoneNumber } from 'Modules/conversation/createConversation/components/createConversationForm/utils';
import type React from 'react';
import { OptionType } from '../index.types';

/**
 * Generic filter options based on the input value
 * @returns The filtered options
 */
export const filterOption = (
  option: { data: OptionType },
  inputValue: string
) => {
  if (!inputValue) {
    return true;
  }

  // Special handling for typed phone numbers - always show them if they match the input
  if (option.data.isTypedPhoneNumber) {
    const phoneValue = cleanPhoneNumber(option.data.value || '');
    const searchTerm = cleanPhoneNumber(inputValue);
    return phoneValue.includes(searchTerm) || searchTerm.includes(phoneValue);
  }

  // Generic text search
  const searchTerm = inputValue.toLowerCase();
  const title = option.data.title?.toLowerCase() || '';
  const description = option.data.description?.toLowerCase() || '';

  return title.includes(searchTerm) || description.includes(searchTerm);
};

/**
 * Highlight text custom style.
 * When the search term is purely numeric, it matches against the text while
 * ignoring non-digit formatting characters (parentheses, spaces, dashes),
 * so that e.g. searching "81468" highlights within "(814) 687-1240".
 */
export function highlightText(
  text: string | undefined,
  searchTerm: string
): React.ReactNode {
  if (!text) return '';
  if (!searchTerm) return text;

  const isNumericSearch = /^\d+$/.test(searchTerm);

  if (isNumericSearch) {
    // Build a regex that allows non-digit characters between each digit
    const pattern = searchTerm.split('').join('[^\\d]*');
    const regex = new RegExp(`(${pattern})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="font-semibold bg-transparent">
          {part}
        </mark>
      ) : (
        part
      )
    );
  }

  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$' + '&');
  const regex = new RegExp(`(${escapedTerm})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="font-semibold bg-transparent">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

/**
 * Creates a phone number option for the autocomplete
 * @param phoneNumber - The phone number string
 * @param isDisabled - Whether the option should be disabled
 * @param tooltipMessage - Optional tooltip message for disabled state
 * @returns ContactOptionType for the phone number
 */
export const createPhoneNumberOption = (
  phoneNumber: string,
  isDisabled = false,
  tooltipMessage?: string
): OptionType => ({
  value: phoneNumber,
  label: phoneNumber,
  title: phoneNumber,
  description: '',
  type: 'unsaved-contact',
  isDisabled,
  tooltipMessage,
  isTypedPhoneNumber: true,
});

/**
 * Creates a free text search option for the autocomplete
 * @param text - The free text string typed by the user
 * @param isDisabled - Whether the option should be disabled
 * @param tooltipMessage - Optional tooltip message for disabled state
 * @returns OptionType for the free text search
 */
export const createFreeTextOption = (
  text: string,
  isDisabled = false,
  tooltipMessage?: string
): OptionType => {
  const trimmed = text.trim();
  return {
    value: trimmed,
    label: trimmed,
    title: trimmed,
    description: '',
    type: 'unsaved-contact',
    isDisabled,
    tooltipMessage,
    isTypedPhoneNumber: false,
  };
};